/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import AsideContainer from '@components/aside/AsideContainer';
import MainContainer from '@components/main/MainContainer';
import { type IOptionProps, type RootData } from '@interface/ICommon';
import { selectLocale } from '@store/commonSlice';
import { useAppSelector } from '@store/index';
import { keepPreviousData } from '@tanstack/react-query';
import { getCookie } from '@utils/common';
import { EVENTTYPE_COOKIE_KEY, MAX_COOKIE_KEY, MIN_COOKIE_KEY, SERVERID_COOKIE_KEY } from '@utils/constants';
import { getWeekDates } from '@utils/scheduleUtils';
import { useEffect, useState } from 'react';
import { Toaster } from 'react-hot-toast';
import { useLoaderData, useSearchParams } from 'react-router-dom';
import { useSchedule, useServerList } from '../hooks/data';

export async function loader({ request, params }: Record<string, any>): Promise<RootData> {
    const url = new URL(request.url);
    const paramDate = url.searchParams.get('date') || null;
    const paramEtype = url.searchParams.get('etype') || null;
    const paramMin = url.searchParams.get('min') || null;
    const paramMax = url.searchParams.get('max') || null;
    const paramServerid = url.searchParams.get('server') || null;

    return { paramDate, paramEtype, paramMin, paramMax, paramServerid };
}

const Root = () => {
    const { paramDate, paramEtype, paramMin, paramMax, paramServerid } = useLoaderData() as RootData;
    const [searchParams, setSearchParams] = useSearchParams();

    const [isShowAsideMenu, setIsShowAsideMenu] = useState(false);
    const [activeServerId, setActiveServerId] = useState<string | null>(null);
    const [activeDate, setActiveDate] = useState<string>('');
    const [activeEvtType, setActiveEvtType] = useState<string | null>(null);
    const [activeMin, setActiveMin] = useState<string | null>(null);
    const [activeMax, setActiveMax] = useState<string | null>(null);

    const locale = useAppSelector(selectLocale);
    const { data: serverListData } = useServerList({ locale });
    const [dateListData, setDateListData] = useState<string[]>(getWeekDates());

    const {
        data: evtListData,
        isFetching,
        refetch,
    } = useSchedule(
        {
            locale,
            date: activeDate,
            scheduleType: activeEvtType,
            minLevel: activeMin,
            maxLevel: activeMax,
        },
        activeServerId as string,
        { staleTime: 5000, placeholderData: keepPreviousData, enabled: !!activeServerId && !!activeDate }
    );

    useEffect(() => {
        const savedEventType = decodeURIComponent(getCookie(EVENTTYPE_COOKIE_KEY) ?? '');
        if (!paramEtype && !!savedEventType) {
            searchParams.set('etype', savedEventType);
            setSearchParams(searchParams);
        }

        setActiveEvtType(paramEtype ?? savedEventType);
        setActiveMin(paramMin ?? getCookie(MIN_COOKIE_KEY) ?? '');
        setActiveMax(paramMax ?? getCookie(MAX_COOKIE_KEY) ?? '');

        if (dateListData) {
            if (paramDate && dateListData.includes(paramDate)) {
                setActiveDate(paramDate);
            } else {
                setActiveDate(dateListData[0]);
            }
        }

        // 서버아이디 우선순위 :: 파람 -> 로그인한 캐릭터 -> 쿠키에 저장된 -> 서버목록의 첫번째 항목
        if (serverListData) {
            const userCharSvrId = window.userData?.gameAccount.serverId || null;
            const cookieSvrId = getCookie(SERVERID_COOKIE_KEY) ?? null;
            const dataSvrId = serverListData[0].servers[0].serverId;
            setActiveServerId(paramServerid ?? userCharSvrId ?? cookieSvrId ?? dataSvrId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateListData, paramDate, paramEtype, paramMax, paramMin, paramServerid, serverListData]);

    const refreshFn = () => {
        refetch();
        setDateListData(getWeekDates());
    };

    const optionProps: IOptionProps = {
        isShowAsideMenu,
        toggleAsideMenu: setIsShowAsideMenu,
        activeEvtType,
        activeMin,
        activeMax,
        activeServerId,
        activeDate,
        serverListData,
        dateListData,
        evtListData,
        isFetching,
        refreshFn,
    };

    return (
        <div className="wrap-schedule relative w-full lg:flex lg:justify-between">
            <AsideContainer props={optionProps} />
            <MainContainer props={optionProps} />
            <div style={{ position: 'fixed', zIndex: 9999999, inset: 0, pointerEvents: 'none' }}>
                <Toaster />
            </div>
        </div>
    );
};

export default Root;
